/* eslint-disable jsx-a11y/alt-text */
import { IonIcon, IonRow, IonText } from '@ionic/react';
import SpinnaxSkater from 'components/assets/SpinnaxSkater';
import HStack from 'components/HStack';
import VStack from 'components/VStack';
import { videocam } from 'ionicons/icons';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';
import { useGeoLocation } from 'services/GeoLocation';
import { RunModel } from '../../../models/RunModel/RunModel';
import { calcHighestSpeed, scaleData } from '../RunListItem/RunListItem';
import { useUnitConversion } from 'hooks/useUnitConversion';
import { useStores } from '../../../models';
import { star, starOutline } from 'ionicons/icons';

interface RunFavoriteCardProps {
  item: RunModel;
  isFriend: boolean;
  onClick?: (item: RunModel) => void;
}
const RunFavoriteCard: React.FC<RunFavoriteCardProps> = observer(({ 
  item,
  onClick 
}) => {
  const { t } = useTranslation();
  const history = useHistory();
  const locator = useGeoLocation();
  const unitConversion = useUnitConversion();
  const { currentUser } = useStores();

  const [location, setLocation] = useState(t('UNKNOWN_LOCATION') as string);
  const theLocation = item.recordData?.location ?? item.playData?.location;

  const starClicked = (ev: React.MouseEvent) => {
    ev.preventDefault();
    ev.stopPropagation();
    if (!item.shareUrl || !currentUser) return;

    currentUser.toggleFavorite(item.shareUrl).then((ret) => {
      if (ret == null) return; // error thrown.
      item!.setFavorite(ret);
    });
    return false;
  };

  useEffect(() => {
    if (!theLocation) {
      setLocation(t('UNKNOWN_LOCATION') as string);
      return;
    }
    if (theLocation.name) {
      setLocation(theLocation.name);
      return;
    }

    locator
      .getAddressFromLocation(theLocation)
      .then(setLocation)
      .catch(() => {});
  }, [theLocation, locator, t]);

  const navigateToDetailPage = () => {
    if (onClick) {
      onClick(item);
    } else {
      history.push(`/trick/${item.id}`);
    }
  };

  const details = item.playData?.details;

  const itemInfo = useMemo(() => {
    // Use unit conversion for all distance measurements
    let jumpDistObj = unitConversion.scaleDistance(details?.fly_width_m);
    let distObj = unitConversion.scaleDistance(details?.travel_distance_m);
    let jumpHeightObj = unitConversion.scaleDistance(details?.fly_height_m);
    let heightDiffObj = unitConversion.scaleDistance(
      details?.height_difference_m
    );

    // Extract formatted strings from objects
    let jumpDist = `${jumpDistObj.value}${jumpDistObj.suffix}`;
    let dist = `${distObj.value}${distObj.suffix}`;
    let jumpHeight = `${jumpHeightObj.value}${jumpHeightObj.suffix}`;
    let heightDiff = `${heightDiffObj.value}${heightDiffObj.suffix}`;
    let airTime = scaleData(details?.air_time_s, 's');

    // Use unit conversion for speed display
    const speedValue = calcHighestSpeed(item.playData?.steps);
    let speed = '?? km/h';
    if (speedValue !== undefined) {
      const displayData = unitConversion.getDisplay(
        speedValue,
        'speed',
        'metric'
      ); // km/h is stored in metric
      speed = displayData.formatted;
    }

    return { jumpDist, dist, jumpHeight, heightDiff, airTime, speed };
  }, [details, item.playData?.steps, unitConversion]);

  let heightDiffIcon = '/assets/icons/height_difference_up.svg';
  if (itemInfo.heightDiff?.startsWith('-')) {
    heightDiffIcon = '/assets/icons/height_difference_down.svg';
  }
  const theTitle = item.recordData?.title ?? item.playData?.title ?? '??'; // TODO save title in firestore?
  const stance = item.recordData?.stance ?? item.playData?.stance ?? '';
  return (
    <div className="favorite-card" onClick={navigateToDetailPage}>
      <div className="card-header">
        <div className="stripe-1" />
        <div className="stripe-2" />
        <SpinnaxSkater className="skater-svg" />
      </div>
      <div className="card-body">
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IonRow className="ion-justify-content-between ion-align-items-center">
            <IonText style={{ gap: '4px', fontSize: 32 }}>
              <b>{theTitle}</b>
            </IonText>
                      {/* FAVORIT ICON */}
          {item.playData && (
            <IonIcon
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                starClicked(e);
              }}
              size="large"
              color="secondary"
              icon={item.favorite ? star : starOutline}
              style={{
                cursor: 'pointer',
                fontSize: '2rem',
                width: '2rem',
                height: '2rem',
              }}
            />
          )}
          </IonRow>
          <IonText className="stance">{stance}</IonText>

          <HStack>
            <div className="trick-stats2">
              <div className="trick-stat">
                <IonIcon src="/assets/icons/jump_distance.svg" />
                <IonText> {itemInfo.jumpDist} </IonText>
              </div>
              <div className="trick-stat">
                <IonIcon src="/assets/icons/distance.svg" />
                <IonText>{itemInfo.dist} </IonText>
              </div>
              <div className="trick-stat">
                <IonIcon src="/assets/icons/jump_height.svg" />
                <IonText>{itemInfo.jumpHeight}</IonText>
              </div>

              <div className="trick-stat">
                <IonIcon src={heightDiffIcon} />
                <IonText> {itemInfo.heightDiff} </IonText>
              </div>
              <div className="trick-stat">
                <IonIcon src="/assets/icons/airtime.svg" />
                <IonText>{itemInfo.airTime} </IonText>
              </div>
              <div className="trick-stat">
                <IonIcon src="/assets/icons/speed2.svg" />
                <IonText>{itemInfo.speed}</IonText>
              </div>
            </div>
            <HStack className="video-preview">
              <div
                style={{
                  backgroundColor: !item.videoUrl ? 'transparent' : undefined,
                }}
                className="video-preview-rect"
              >
                {item.videoUrl && <IonIcon icon={videocam} />}
              </div>
              <div className="skater-name">
                <span>{item.skater}</span>
              </div>
            </HStack>
          </HStack>
          <VStack>
            <HStack
              style={{
                width: '70%',
                marginTop: '8px',
                justifyContent: 'space-between',
              }}
            >
              <IonText
                style={{
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {location}
              </IonText>
              <div style={{ flexGrow: '1' }} />
              <IonText
                style={{
                  display: 'none',
                  marginRight: '16px',
                  whiteSpace: 'nowrap',
                }}
              >
                {item.date.toLocaleString()}
              </IonText>
            </HStack>
          </VStack>
        </div>
      </div>
    </div>
  );
});

export default RunFavoriteCard;
